/**
 * Estado "logado": barra de navegação à esquerda + subestado à direita.
 * Estrutura: docs/spec/screens/TfrmLoggedStateForm.md
 *
 * Da análise do binário (FC.InterfaceState.LoggedStateForm):
 * - largura da barra = 64 px; altura do botão = round(64 × 0,7714) = 49 px;
 * - botões padrão, nesta ordem: dashboard, salas, contatos, Desk Cloud, configurações. No RPG Desk não
 *   há o de contatos: os amigos ficam no painel "Amigos e chat" (social/FriendsDock.tsx), por cima de
 *   qualquer tela, no canto de baixo à direita, que abre pelo próprio cabeçalho (docs/spec/friends.md);
 * - cada sala aberta ganha um botão próprio, com badge de mensagens não lidas. O botão direito nele
 *   abre o menu da mesa (convidar, configurações, Painéis, sair; `room/roomMenu.ts`); numa mesa que
 *   não está à vista, só o "Sair da mesa";
 * - a área do subestado tem margens 0/8/8/8 (loggedInWindowStyle).
 *
 * No celular (docs/spec/mobile.md), a barra fica embaixo (Início, Salas, Amigos, Arquivos e Mais)
 * e as mesas abertas viram uma faixa logo acima dela.
 */
import { useEffect, useState } from 'react';
import { useT } from '../i18n/index.js';
import { useApp } from '../store.js';
import { Icon, type IconName } from '../ui/icons.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../ui/ContextMenu.js';
import { useMobile } from '../ui/mobile.js';
import { useLongPress } from '../ui/touch.js';
import { roomInitials, type OpenRoom } from '../room/openRooms.js';
import { roomImageSrc } from '../explore/roomImages.js';
import { ClockRingArc, useClockRing } from '../room/clock/ClockRing.js';
import { formatRingTime } from '../room/clock/clockModel.js';
import { roomMenuEntries } from '../room/roomMenu.js';
import { DiceLayer } from '../dice/DiceLayer.js';
import { RoomDesktop } from '../room/RoomDesktop.js';
import { FriendsDock } from '../social/FriendsDock.js';
import { useFriendsUi } from '../social/friendsUi.js';
import { unreadTotal } from '../social/friendsModel.js';
import { useAwayReporter } from '../social/idle.js';
import { ProfilePopupHost } from '../social/ProfilePopup.js';
import { ExploreState } from '../explore/ExploreState.js';
import { HomeState } from '../explore/HomeState.js';
import { DeskCloudState } from '../cloud/DeskCloudState.js';
import { ConfigurationState } from '../config/ConfigurationState.js';
import { AdminState } from '../admin/AdminState.js';
import { useLoggedNav, type LoggedSubState } from './nav.js';
import './logged.css';


type StdButton = { kind: Exclude<LoggedSubState['kind'], 'room' | 'admin'>; icon: IconName; hint: string };

const STANDARD: readonly StdButton[] = [
  { kind: 'dashboard', icon: 'dashboard', hint: 'logged.nav.dashboard' },
  { kind: 'rooms', icon: 'rooms', hint: 'logged.nav.rooms' },
  { kind: 'cloud', icon: 'cloud', hint: 'logged.nav.cloud' },
];

export function LoggedState() {
  const t = useT();
  const { sub, go } = useLoggedNav();
  const activeRoomId = useApp((s) => s.activeRoomId);
  const openRooms = useApp((s) => s.openRooms);
  const leaveOpenRoom = useApp((s) => s.leaveOpenRoom);
  const reopenRooms = useApp((s) => s.reopenRooms);
  const [roomMenu, setRoomMenu] = useState<{ roomId: string; anchor: MenuAnchor } | null>(null);
  const friendsUnread = useApp((s) => unreadTotal(s.contacts));
  // o painel pode ser desligado em Configurações → Geral
  const showFriends = useApp((s) => s.settings.showFriendsChat);
  const friendsOpen = useFriendsUi((s) => s.open);
  const toggleFriends = useFriendsUi((s) => s.toggle);
  const isAdmin = useApp((s) => s.isAdmin);
  const setFriendsOpen = useFriendsUi((s) => s.setOpen);
  useAwayReporter();
  const logout = useApp((s) => s.logout);
  const settings = useApp((s) => s.settings);
  const mobile = useMobile();
  const [moreMenu, setMoreMenu] = useState<MenuAnchor | null>(null);

  // Recarregar a página reabre as mesas que estavam abertas nesta aba, com a mesma na tela.
  // Link de mesa (?room=<id>, do "copiar link da mesa"): ela entra na tela e o endereço é limpo.
  const joinRoom = useApp((s) => s.joinRoom);
  const userId = useApp((s) => s.user?.id ?? null);
  useEffect(() => {
    const saved = readSessionRooms(userId);
    const params = new URLSearchParams(window.location.search);
    const linked = params.get('room');
    if (linked) {
      params.delete('room');
      const rest = params.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
    }
    reopenRooms(saved.rooms, linked ?? saved.active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinRoom, reopenRooms]);
  useEffect(() => {
    writeSessionRooms({ rooms: openRooms.map((open) => open.roomId), active: activeRoomId, user: userId });
  }, [openRooms, activeRoomId, userId]);

  // Página inicial configurável (preferências herdadas do Mesaviva).
  useEffect(() => {
    if (settings.startPage === 'salas') go({ kind: 'rooms' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Entrar numa sala abre o botão dela e seleciona; sair volta ao dashboard.
  useEffect(() => {
    if (activeRoomId) go({ kind: 'room', roomId: activeRoomId });
    else if (sub.kind === 'room') go({ kind: 'dashboard' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoomId]);

  // a área de administração some com o papel (rebaixado agora, ou outra conta entrou nesta aba)
  useEffect(() => {
    if (sub.kind === 'admin' && !isAdmin) go({ kind: 'dashboard' });
  }, [sub.kind, isAdmin, go]);

  const openRoom = (roomId: string) => (roomId === activeRoomId ? go({ kind: 'room', roomId }) : joinRoom(roomId));
  // o menu da mesa vem dela, quando é a montada; os itens (menos o "Sair") trazem a mesa para a
  // frente antes de agir
  const roomMenuView = roomMenu
    ? (() => {
        const name = openRooms.find((open) => open.roomId === roomMenu.roomId)?.name || t('logged.nav.room');
        const own = roomMenuEntries(roomMenu.roomId);
        const show = (entry: MenuEntry): MenuEntry =>
          entry.kind === 'submenu'
            ? { ...entry, entries: entry.entries.map(show) }
            : (entry.kind === undefined || entry.kind === 'item') && !entry.danger
              ? {
                  ...entry,
                  onSelect: () => {
                    go({ kind: 'room', roomId: roomMenu.roomId });
                    entry.onSelect();
                  },
                }
              : entry;
        const entries: MenuEntry[] = own
          ? own.map(show)
          : [{ label: t('logged.rooms.leave'), danger: true, icon: <Icon name="logout" size={14} />, onSelect: () => leaveOpenRoom(roomMenu.roomId) }];
        return <ContextMenu anchor={roomMenu.anchor} label={name} entries={[{ kind: 'title', label: name }, ...entries]} onClose={() => setRoomMenu(null)} />;
      })()
    : null;

  return (
    <div className={`rd-logged${mobile ? ' rd-logged--mobile' : ''}`}>
      {mobile ? null : (
      <nav className="rd-logged__nav" aria-label={t('logged.nav.label')}>
        <div className="rd-logged__navTop">
          {STANDARD.map((b) => (
            <NavButton key={b.kind} icon={b.icon} hint={t(b.hint)} selected={sub.kind === b.kind} onClick={() => go({ kind: b.kind })} />
          ))}
        </div>

        <div className="rd-logged__navRooms" aria-label={t('logged.nav.openRooms')}>
          {openRooms.map((open) => (
            <RoomButton
              key={open.roomId}
              room={open}
              selected={sub.kind === 'room' && open.roomId === activeRoomId}
              onClick={() => openRoom(open.roomId)}
              onMenu={(anchor) => setRoomMenu({ roomId: open.roomId, anchor })}
            />
          ))}
        </div>
        {roomMenuView}

        <div className="rd-logged__navBottom">
          {isAdmin ? <NavButton icon="shield" hint={t('logged.nav.admin')} selected={sub.kind === 'admin'} onClick={() => go({ kind: 'admin' })} /> : null}
          <NavButton icon="settings" hint={t('logged.nav.settings')} selected={sub.kind === 'settings'} onClick={() => go({ kind: 'settings' })} />
          <NavButton icon="logout" hint={t('logged.nav.logout')} selected={false} onClick={() => void logout()} />
        </div>
      </nav>
      )}

      <main className="rd-logged__content">
        <div className="rd-logged__window" hidden={sub.kind === 'room'}>
          {sub.kind === 'dashboard' ? (
            <HomeState
              onNavigate={(target) => {
                if (target.kind === 'room') go(target);
                else go({ kind: 'rooms', ...(target.tab ? { tab: target.tab } : {}), ...(target.query ? { query: target.query } : {}), ...(target.system ? { system: target.system } : {}) });
              }}
            />
          ) : null}
          {/* outra busca ou outro sistema (do Início) recomeça o Explorar com eles */}
          {sub.kind === 'rooms' ? <ExploreState key={`${sub.tab ?? ''}|${sub.query ?? ''}|${sub.system ?? ''}`} initialTab={sub.tab ?? 'search'} initialQuery={sub.query} initialSystem={sub.system} /> : null}
          {sub.kind === 'cloud' ? <DeskCloudState /> : null}
          {sub.kind === 'settings' ? <ConfigurationState initialPage={sub.page} initialRow={sub.row} /> : null}
          {sub.kind === 'admin' && isAdmin ? <AdminState initialPage={sub.page} /> : null}
        </div>
        {/* a mesa continua montada fora da aba dela: música, voz e fichas não podem cair */}
        {activeRoomId ? (
          <div className="rd-logged__window rd-logged__window--room" hidden={sub.kind !== 'room'}>
            {/* trocar de mesa remonta a área de trabalho: nada da anterior fica pendurado */}
            <RoomDesktop key={activeRoomId} />
          </div>
        ) : null}
        {/* os dados 3D da mesa por cima de tudo, também quando outra tela está aberta: rolam sempre */}
        {activeRoomId ? <DiceLayer key={activeRoomId} /> : null}
      </main>
      {mobile ? (
        <>
          {openRooms.length && sub.kind !== 'room' ? (
            <RoomStrip rooms={openRooms} activeRoomId={activeRoomId} onOpen={openRoom} onMenu={(roomId, anchor) => setRoomMenu({ roomId, anchor })} />
          ) : null}
          {roomMenuView}
          {/* dentro da mesa, a barra de baixo é a dela (Chat, Cena, Fichas…), com o voltar no topo */}
          {sub.kind !== 'room' ? (
            <nav className="rd-mnav" aria-label={t('logged.nav.label')}>
              {/* Início, Salas, Amigos (abre o painel em tela cheia), Arquivos */}
              {STANDARD.flatMap((b) => [
                ...(b.kind === 'cloud' && showFriends ? [<MobileNavButton key="friends" icon="contacts" label={t('logged.nav.friends')} selected={friendsOpen} badge={friendsUnread} onClick={toggleFriends} />] : []),
                <MobileNavButton
                  key={b.kind}
                  icon={b.icon}
                  label={t(b.hint)}
                  selected={!(showFriends && friendsOpen) && sub.kind === b.kind}
                  onClick={() => {
                    setFriendsOpen(false);
                    go({ kind: b.kind });
                  }}
                />,
              ])}
              <MobileNavButton icon="menu" label={t('logged.nav.more')} selected={sub.kind === 'settings' || sub.kind === 'admin' || !!moreMenu} onClick={(e) => setMoreMenu({ x: e.clientX, y: e.clientY })} />
            </nav>
          ) : null}
          {moreMenu ? (
            <ContextMenu
              anchor={moreMenu}
              label={t('logged.nav.more')}
              entries={[
                ...(isAdmin ? [{ label: t('logged.nav.admin'), icon: <Icon name="shield" size={18} />, onSelect: () => go({ kind: 'admin' }) }] : []),
                { label: t('logged.nav.settings'), icon: <Icon name="settings" size={18} />, onSelect: () => go({ kind: 'settings' }) },
                { kind: 'separator' },
                { label: t('logged.nav.logout'), icon: <Icon name="logout" size={18} />, danger: true, onSelect: () => void logout() },
              ]}
              onClose={() => setMoreMenu(null)}
            />
          ) : null}
        </>
      ) : null}
      {showFriends ? <FriendsDock inRoom={sub.kind === 'room'} /> : null}
      <ProfilePopupHost />
    </div>
  );
}

/** Celular: um botão da barra de baixo, com o nome embaixo do ícone. */
function MobileNavButton({ icon, label, selected, badge = 0, onClick }: { icon: IconName; label: string; selected: boolean; badge?: number; onClick: (e: React.MouseEvent) => void }) {
  return (
    <button type="button" className={`rd-mnav__btn${selected ? ' is-on' : ''}`} aria-label={badge ? `${label} (${badge})` : label} aria-current={selected ? 'page' : undefined} onClick={onClick}>
      <span className="rd-mnav__icon">
        <Icon name={icon} size={24} strokeWidth={1.8} />
        {badge ? <span className="rd-navbtn__badge">{badge > 99 ? '99+' : badge}</span> : null}
      </span>
      <span className="rd-mnav__label" aria-hidden="true">
        {label}
      </span>
    </button>
  );
}

/** Celular: as mesas abertas, acima da barra de baixo. Tocar volta a ela; segurar dá o "sair". */
function RoomStrip({ rooms, activeRoomId, onOpen, onMenu }: { rooms: OpenRoom[]; activeRoomId: string | null; onOpen: (roomId: string) => void; onMenu: (roomId: string, anchor: MenuAnchor) => void }) {
  const t = useT();
  const press = useLongPress();
  return (
    <div className="rd-roomStrip" role="navigation" aria-label={t('logged.nav.openRooms')}>
      {rooms.map((room) => {
        const name = room.name || t('logged.nav.room');
        return (
          <button
            key={room.roomId}
            type="button"
            className={`rd-roomStrip__room${room.roomId === activeRoomId ? ' is-active' : ''}`}
            aria-label={[t('logged.rooms.back', { name }), room.unread > 0 ? t('logged.rooms.unread', { count: room.unread }) : '', room.mention ? t('logged.rooms.mention') : ''].filter(Boolean).join(', ')}
            {...press.bind((anchor) => onMenu(room.roomId, anchor))}
            onClick={() => onOpen(room.roomId)}
          >
            <span className="rd-roomStrip__avatar" aria-hidden="true">
              <RoomLogo room={room} />
            </span>
            <span className="rd-roomStrip__name" aria-hidden="true">
              {name}
            </span>
            {room.unread > 0 ? (
              <span className={`rd-navbtn__badge rd-roomStrip__badge${room.mention ? ' rd-navbtn__badge--mention' : ''}`} aria-hidden="true">
                {room.unread > 99 ? '99+' : room.unread}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Botão de uma mesa aberta: iniciais, não lidas e o menu para sair dela. Com uma sessão de jogo aberta,
 * o anel do relógio em volta das iniciais e os minutos embaixo (docs/spec/session-clock.md), se a mesa
 * não o desligou.
 */
function RoomButton({ room, selected, onClick, onMenu }: { room: OpenRoom; selected: boolean; onClick: () => void; onMenu: (anchor: MenuAnchor) => void }) {
  const t = useT();
  const press = useLongPress();
  const ring = useClockRing(room.ring ? room.clock : null);
  const name = room.name || t('logged.nav.room');
  const unread = room.unread > 0 ? t('logged.rooms.unread', { count: room.unread }) : '';
  const played = ring ? formatRingTime(t, ring.playedMs) : '';
  const clock = ring ? t(ring.paused ? 'logged.rooms.clock.paused' : 'logged.rooms.clock.running', { n: ring.number, played }) : '';
  const label = [name, unread, room.mention ? t('logged.rooms.mention') : '', clock].filter(Boolean).join(', ');
  return (
    <button
      type="button"
      className={`rd-navbtn rd-navbtn--room${selected ? ' rd-navbtn--selected' : ''}${ring ? ' has-clock' : ''}`}
      title={name}
      aria-label={label}
      aria-current={selected ? 'page' : undefined}
      onClick={onClick}
      {...press.bind(onMenu)}
    >
      <span className="rd-navbtn__avatar">
        <RoomLogo room={room} />
        {ring ? <ClockRingArc progress={ring.progress} paused={ring.paused} /> : null}
      </span>
      {ring ? (
        <span className={`rd-clockRing__minutes${ring.paused ? ' is-paused' : ''}`} aria-hidden="true">
          {played}
        </span>
      ) : null}
      {room.unread > 0 ? <span className={`rd-navbtn__badge${room.mention ? ' rd-navbtn__badge--mention' : ''}`}>{room.unread > 99 ? '99+' : room.unread}</span> : null}
    </button>
  );
}

/** A marca da mesa, recortada no formato do botão; sem marca (ou se ela não abre), as iniciais (BRAZ-33). */
function RoomLogo({ room }: { room: OpenRoom }) {
  const src = roomImageSrc(room.roomId, 'logo', room.logoUrl);
  const [failed, setFailed] = useState<string | null>(null);
  if (src && src !== failed) return <img className="rd-roomLogo" src={src} alt="" draggable={false} onError={() => setFailed(src)} />;
  return <>{roomInitials(room.name || '?')}</>;
}

function NavButton({ icon, hint, selected, badge = 0, onClick }: { icon: IconName; hint: string; selected: boolean; badge?: number; onClick: () => void }) {
  return (
    <button
      type="button"
      className={`rd-navbtn${selected ? ' rd-navbtn--selected' : ''}`}
      title={hint}
      aria-label={badge ? `${hint} (${badge})` : hint}
      aria-current={selected ? 'page' : undefined}
      onClick={onClick}
    >
      <Icon name={icon} size={26} strokeWidth={1.7} />
      {badge ? <span className="rd-navbtn__badge">{badge > 99 ? '99+' : badge}</span> : null}
    </button>
  );
}

/** O Dashboard herdado do Mesaviva navega por nomes antigos de tela. */

/**
 * Mesas abertas nesta aba do navegador e a da tela (sobrevivem a recarregar, não a abrir outra aba).
 * Levam a conta: sair dela e entrar com outra na mesma aba não reabre as mesas da anterior.
 */
const SESSION_ROOMS = 'rpgdesk.openRooms';
type SessionRooms = { rooms: string[]; active: string | null; user?: string | null };
function readSessionRooms(userId: string | null): SessionRooms {
  try {
    const raw = JSON.parse(window.sessionStorage.getItem(SESSION_ROOMS) ?? 'null') as Partial<SessionRooms> | null;
    // de outra conta (sem `user`, de antes desta marca, vale para quem estiver)
    if (raw?.user && raw.user !== userId) return { rooms: [], active: null };
    const rooms = Array.isArray(raw?.rooms) ? raw.rooms.filter((id): id is string => typeof id === 'string') : [];
    // antes de várias mesas, só a da tela era guardada
    const old = window.sessionStorage.getItem('rpgdesk.openRoom');
    if (!rooms.length && old) return { rooms: [old], active: old };
    return { rooms, active: typeof raw?.active === 'string' ? raw.active : null };
  } catch {
    return { rooms: [], active: null };
  }
}
function writeSessionRooms(saved: SessionRooms): void {
  try {
    window.sessionStorage.removeItem('rpgdesk.openRoom');
    if (saved.rooms.length || saved.active) window.sessionStorage.setItem(SESSION_ROOMS, JSON.stringify(saved));
    else window.sessionStorage.removeItem(SESSION_ROOMS);
  } catch {
    // armazenamento indisponível: recarregar volta ao início
  }
}
